import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Putting a look (`appearance.js`) on a body (`figure.js`): hair, a face,
 * a hat, the pieces of a uniform, a name tag, and the clothes' colours.
 *
 * Everything is built in code, low-poly, and costs no download. Each piece is
 * built in the body's bind space (the T, metres, +Y up, +Z the way the body
 * faces, +X its left), then carried into the space of the bone it rides on
 * by that bone's inverse bind matrix, so it moves exactly as the skin under
 * it does. Pieces on one bone are merged into one mesh with its colours in
 * the vertices, and every person's pieces share one material: a person is at
 * most three more draw calls (head, chest, hips).
 *
 * The clothes are one material with three zones read off the bind position of
 * each vertex (`clothMaterial`): the torso, the sleeves and the legs, each its
 * own colour, with bare skin past a short sleeve or a pair of shorts.
 *
 * None of it is collided with, seen by anybody, or read by anything.
 */

/** One material for every piece on everybody: the colour is in the vertices. */
let pieceMaterial = null;
function piecesMaterial() {
  pieceMaterial ??= new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0 });
  return pieceMaterial;
}

/**
 * The few things about the body every piece is fitted to, measured once off
 * the bind geometry: the head as an ellipsoid, the face's front at a height,
 * the front of the chest at a height, and the waist.
 */
export function measureBody(parts) {
  const of = (region) => parts.filter((p) => p.region === region).map((p) => p.geometry.attributes.position);
  const skin = of('skin');
  const hair = of('hair');
  const cloth = of('cloth');
  const each = (lists, fn) => {
    for (const list of lists) for (let i = 0; i < list.count; i += 1) fn(list.getX(i), list.getY(i), list.getZ(i));
  };
  // The head: everything of skin and hair above the jaw.
  let top = -Infinity;
  each([...skin, ...hair], (x, y) => {
    if (Math.abs(x) < 0.15) top = Math.max(top, y);
  });
  const jaw = top - 0.235;
  const box = { x0: Infinity, x1: -Infinity, z0: Infinity, z1: -Infinity };
  each(hair.length ? hair : skin, (x, y, z) => {
    if (y < jaw + 0.08 || Math.abs(x) > 0.15) return;
    box.x0 = Math.min(box.x0, x);
    box.x1 = Math.max(box.x1, x);
    box.z0 = Math.min(box.z0, z);
    box.z1 = Math.max(box.z1, z);
  });
  const head = {
    c: [(box.x0 + box.x1) / 2, (top + jaw) / 2 + 0.01, (box.z0 + box.z1) / 2],
    r: [(box.x1 - box.x0) / 2, (top - jaw) / 2, (box.z1 - box.z0) / 2],
    top,
    jaw,
  };
  const frontOf = (lists, y0, band = 0.012, half = 0.025) => {
    let best = -Infinity;
    each(lists, (x, y, z) => {
      if (Math.abs(y - y0) < band && Math.abs(x - head.c[0]) < half) best = Math.max(best, z);
    });
    return Number.isFinite(best) ? best : head.c[2] + head.r[2];
  };
  const face = (y) => frontOf(skin, y);
  const chest = (y) => frontOf(cloth, y, 0.015, 0.04);
  // The waist, at the hips' height.
  let waistY = 1.0;
  const w = { x: 0, z0: Infinity, z1: -Infinity };
  each(cloth, (x, y, z) => {
    if (Math.abs(y - waistY) > 0.02 || Math.abs(x) > 0.3) return;
    w.x = Math.max(w.x, Math.abs(x));
    w.z0 = Math.min(w.z0, z);
    w.z1 = Math.max(w.z1, z);
  });
  return { head, face, chest, waist: { y: waistY, rx: w.x || 0.17, z0: w.z0, z1: w.z1, cz: (w.z0 + w.z1) / 2, rz: (w.z1 - w.z0) / 2 || 0.12 } };
}

/** Paints a geometry one colour, in its vertices. */
function paint(geometry, hex) {
  const colour = new THREE.Color(hex);
  const n = geometry.attributes.position.count;
  const data = new Float32Array(n * 3);
  for (let i = 0; i < n; i += 1) {
    data[i * 3] = colour.r;
    data[i * 3 + 1] = colour.g;
    data[i * 3 + 2] = colour.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(data, 3));
  return geometry;
}

/** Strips a geometry to what merging needs: position, normal, colour. */
function plain(geometry) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'color'].includes(name)) g.deleteAttribute(name);
  return g;
}

/** A stable little noise off a key, for curls. */
function noiser(key) {
  let h = 2166136261;
  for (const ch of String(key)) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return (x, y, z) => {
    const s = Math.sin(x * 91.7 + y * 47.3 + z * 63.1 + (h % 1000)) * 43758.5453;
    return s - Math.floor(s);
  };
}

/**
 * A cap of an ellipsoid round the head: the top of a sphere down to `reach`
 * (a share of the way from the crown to the bottom), tipped back by `tilt` so
 * it comes lower at the back than over the brow, scaled to the head times `k`.
 */
function capOf(m, { k = 1.06, reach = 0.52, tilt = 0.35, ky = 1, lift = 0, back = 0, open = 0, segments = 14 }) {
  const { c, r } = m.head;
  let g;
  if (open > 0) {
    // Open at the face: a gap `open` of the way round, centred on the front.
    const gap = open * Math.PI * 2;
    g = new THREE.SphereGeometry(1, segments, Math.round(segments * 0.7), Math.PI / 2 + gap / 2, Math.PI * 2 - gap, 0, Math.PI * reach);
  } else {
    g = new THREE.SphereGeometry(1, segments, Math.round(segments * 0.7), 0, Math.PI * 2, 0, Math.PI * reach);
  }
  g.rotateX(-tilt);
  g.scale(r[0] * k, r[1] * k * ky, r[2] * k);
  g.translate(c[0], c[1] + lift, c[2] - back);
  return g;
}

function ellipsoid(cx, cy, cz, rx, ry, rz, segments = 10) {
  const g = new THREE.SphereGeometry(1, segments, Math.round(segments * 0.75));
  g.scale(rx, ry, rz);
  g.translate(cx, cy, cz);
  return g;
}

function box(cx, cy, cz, sx, sy, sz) {
  const g = new THREE.BoxGeometry(sx, sy, sz);
  g.translate(cx, cy, cz);
  return g;
}

/** The hair, as geometry in bind space. */
function hairPieces(m, style, colour, key, hatted) {
  const { c, r } = m.head;
  const out = [];
  const add = (g) => out.push(paint(g, colour));
  // Under a hat, anything that would stand up through it is cut back to a
  // crop, and no hair is fuller than the hat's own crown.
  const s = hatted && ['afro', 'hightop', 'bun', 'quiff', 'curly', 'sidepart'].includes(style) ? 'buzz' : style;
  const cap = (mm, o) => capOf(mm, hatted ? { ...o, k: Math.min(o.k ?? 1.06, 1.03) } : o);
  switch (s) {
    case 'buzz':
      add(cap(m, { k: 1.025, reach: 0.5, tilt: 0.32 }));
      break;
    case 'crop':
      add(cap(m, { k: 1.06, reach: 0.52, tilt: 0.36 }));
      break;
    case 'sidepart':
      add(cap(m, { k: 1.06, reach: 0.53, tilt: 0.38 }));
      add(ellipsoid(c[0] + r[0] * 0.35, c[1] + r[1] * 0.78, c[2] + r[2] * 0.15, r[0] * 0.7, r[1] * 0.28, r[2] * 0.8));
      break;
    case 'slick':
      add(cap(m, { k: 1.045, reach: 0.6, tilt: 0.6, ky: 0.98 }));
      break;
    case 'quiff':
      add(cap(m, { k: 1.06, reach: 0.52, tilt: 0.4 }));
      add(ellipsoid(c[0], c[1] + r[1] * 0.85, c[2] + r[2] * 0.55, r[0] * 0.72, r[1] * 0.32, r[2] * 0.45));
      break;
    case 'curly': {
      const g = cap(m, { k: 1.13, reach: 0.56, tilt: 0.4, segments: 22 });
      const p = g.attributes.position;
      const n = noiser(key);
      for (let i = 0; i < p.count; i += 1) {
        const x = p.getX(i) - c[0];
        const y = p.getY(i) - c[1];
        const z = p.getZ(i) - c[2];
        const bump = 1 + (n(x * 3, y * 3, z * 3) - 0.5) * 0.22;
        p.setXYZ(i, c[0] + x * bump, c[1] + y * bump, c[2] + z * bump);
      }
      g.computeVertexNormals();
      add(g);
      break;
    }
    case 'afro': {
      const g = cap(m, { k: 1.3, reach: 0.6, tilt: 0.3, lift: r[1] * 0.12, back: r[2] * 0.08, segments: 18 });
      const p = g.attributes.position;
      const n = noiser(`${key}afro`);
      for (let i = 0; i < p.count; i += 1) {
        const x = p.getX(i) - c[0];
        const y = p.getY(i) - c[1];
        const z = p.getZ(i) - c[2];
        const bump = 1 + (n(x * 2, y * 2, z * 2) - 0.5) * 0.09;
        p.setXYZ(i, c[0] + x * bump, c[1] + y * bump, c[2] + z * bump);
      }
      g.computeVertexNormals();
      add(g);
      break;
    }
    case 'bun':
      add(cap(m, { k: 1.05, reach: 0.55, tilt: 0.5 }));
      add(ellipsoid(c[0], c[1] + r[1] * 0.62, c[2] - r[2] * 0.95, r[0] * 0.48, r[1] * 0.4, r[2] * 0.42));
      break;
    case 'ponytail':
      add(cap(m, { k: 1.05, reach: 0.6, tilt: 0.55 }));
      add(ellipsoid(c[0], c[1] - r[1] * 0.35, c[2] - r[2] * 1.1, r[0] * 0.32, r[1] * 0.85, r[2] * 0.3));
      break;
    case 'long': {
      add(cap(m, { k: 1.07, reach: 0.6, tilt: 0.45 }));
      // A fall of hair down the back to the shoulders, and either side of the face.
      add(box(c[0], c[1] - r[1] * 0.75, c[2] - r[2] * 0.82, r[0] * 1.9, r[1] * 1.55, r[2] * 0.3));
      for (const side of [-1, 1]) add(box(c[0] + side * r[0] * 0.92, c[1] - r[1] * 0.45, c[2] - r[2] * 0.1, r[0] * 0.28, r[1] * 1.0, r[2] * 1.05));
      break;
    }
    case 'bob':
      add(cap(m, { k: 1.09, reach: 0.72, tilt: 0.25, open: 0.44, segments: 18 }));
      break;
    case 'hightop': {
      add(cap(m, { k: 1.05, reach: 0.5, tilt: 0.3 }));
      const g = new THREE.CylinderGeometry(r[0] * 0.95, r[0] * 1.02, r[1] * 0.85, 12);
      g.translate(c[0], c[1] + r[1] * 1.02, c[2] - r[2] * 0.12);
      add(g);
      break;
    }
    default:
      break;
  }
  return out;
}

/** Facial hair, as geometry in bind space. */
function facePieces(m, face, colour, skin) {
  const { c, r } = m.head;
  const out = [];
  if (!face) return out;
  // Off the bind: the nose about two thirds of the way down from the crown to
  // the jaw, the top lip under it, the chin at the jaw.
  const mouth = c[1] - r[1] * 0.62;
  const chinY = c[1] - r[1] * 0.92;
  const lip = Math.min(m.face(mouth + 0.012), m.face(mouth + 0.004));
  const chin = m.face(chinY);
  const stache = () => out.push(paint(box(c[0], mouth + 0.01, lip + 0.002, 0.05, 0.01, 0.012), colour));
  if (face === 'moustache') stache();
  if (face === 'goatee') {
    stache();
    out.push(paint(box(c[0], chinY + 0.012, chin - 0.004, 0.036, 0.034, 0.018), colour));
  }
  if (face === 'beard' || face === 'stubble') {
    const tint = face === 'beard' ? colour : blend(skin, colour, 0.45);
    const k = face === 'beard' ? 1.13 : 1.04;
    // The lower front of the head: from ear to ear, from the cheekbones down.
    // Below the nose: the shell starts under the cheekbones and wraps the jaw.
    const g = new THREE.SphereGeometry(1, 16, 10, Math.PI * 0.06, Math.PI * 0.88, Math.PI * 0.64, Math.PI * 0.3);
    g.scale(r[0] * k, r[1] * k * 1.05, r[2] * k * 1.03);
    g.translate(c[0], c[1] - r[1] * 0.1, c[2] + r[2] * 0.02);
    out.push(paint(g, tint));
    if (face === 'beard') stache();
  }
  return out;
}

function blend(a, b, t) {
  return new THREE.Color(a).lerp(new THREE.Color(b), t).getHex();
}

/** Hats and glasses, as geometry in bind space. */
function headwear(m, pieces, outfit) {
  const { c, r, top } = m.head;
  const out = [];
  const has = (p) => pieces.includes(p);
  if (has('toque')) {
    const band = new THREE.CylinderGeometry(r[0] * 1.08, r[0] * 1.06, 0.05, 16);
    band.translate(c[0], c[1] + r[1] * 0.55, c[2] - r[2] * 0.02);
    out.push(paint(band, 0xf6f4ef));
    const tall = new THREE.CylinderGeometry(r[0] * 1.18, r[0] * 1.06, 0.16, 16);
    tall.translate(c[0], c[1] + r[1] * 0.55 + 0.1, c[2] - r[2] * 0.02);
    out.push(paint(tall, 0xf6f4ef));
    out.push(paint(ellipsoid(c[0], top + 0.13, c[2] - r[2] * 0.02, r[0] * 1.3, 0.05, r[2] * 1.2, 12), 0xfbfaf6));
  }
  if (has('cap') || has('valetcap')) {
    const colour = outfit.cap ?? outfit.hat ?? 0x3b4a5e;
    out.push(paint(capOf(m, { k: 1.17, reach: 0.42, tilt: 0.1, lift: 0.006 }), colour));
    const brim = new THREE.CylinderGeometry(r[0] * 0.95, r[0] * 0.95, 0.008, 12, 1, false, -Math.PI * 0.5, Math.PI);
    brim.scale(1, 1, 0.75);
    brim.translate(c[0], c[1] + r[1] * 0.42, c[2] + r[2] * 0.82);
    out.push(paint(brim, has('valetcap') ? 0x111111 : colour));
    if (has('valetcap')) {
      const crown = new THREE.CylinderGeometry(r[0] * 1.2, r[0] * 1.08, 0.06, 16);
      crown.translate(c[0], c[1] + r[1] * 0.8, c[2]);
      out.push(paint(crown, colour));
      const band = new THREE.CylinderGeometry(r[0] * 1.11, r[0] * 1.11, 0.014, 16);
      band.translate(c[0], c[1] + r[1] * 0.62, c[2]);
      out.push(paint(band, 0xd8b25a));
    }
  }
  if (has('sunhat')) {
    const colour = outfit.hat ?? 0xe8dcc0;
    out.push(paint(capOf(m, { k: 1.18, reach: 0.45, tilt: 0.12, lift: 0.012 }), colour));
    const brim = new THREE.CylinderGeometry(r[0] * 2.3, r[0] * 2.4, 0.01, 20);
    brim.translate(c[0], c[1] + r[1] * 0.4, c[2]);
    out.push(paint(brim, colour));
  }
  if (has('glasses') || has('sunglasses')) {
    const eye = c[1] - r[1] * 0.12;
    const front = m.face(eye) + 0.012;
    const frame = has('sunglasses') ? 0x111111 : 0x2a2522;
    for (const side of [-1, 1]) {
      const ring = new THREE.TorusGeometry(0.015, 0.0016, 4, 12);
      ring.translate(c[0] + side * 0.032, eye, front);
      out.push(paint(ring, frame));
      if (has('sunglasses')) {
        const lens = new THREE.CircleGeometry(0.015, 12);
        lens.translate(c[0] + side * 0.032, eye, front + 0.001);
        out.push(paint(lens, 0x151a20));
      }
      const arm = box(c[0] + side * r[0] * 1.0, eye, c[2] + r[2] * 0.25, 0.003, 0.003, r[2] * 1.4);
      out.push(paint(arm, frame));
    }
    out.push(paint(box(c[0], eye + 0.003, front, 0.034, 0.003, 0.003), frame));
  }
  return out;
}

/** What goes on the chest: the tag, the tie, the shirt and lapels, buttons. */
function chestPieces(m, pieces, outfit, tag) {
  const out = [];
  const has = (p) => pieces.includes(p);
  const front = (y) => m.chest(y) + 0.006;
  const x0 = m.head.c[0];
  const neck = m.head.jaw - 0.05;
  if (has('shirt') || has('vest')) {
    // The shirt in the open front of a jacket or vest: a V from the collar down.
    const shirt = outfit.shirt ?? outfit.sleeves ?? 0xf4f2ee;
    const v = new THREE.BufferGeometry();
    const yTop = neck + 0.015;
    const yBot = has('vest') ? neck - 0.17 : neck - 0.21;
    const zTop = front(yTop - 0.03) - 0.004;
    const zBot = front(yBot);
    v.setAttribute('position', new THREE.Float32BufferAttribute([x0 - 0.06, yTop, zTop, x0 + 0.06, yTop, zTop, x0, yBot, zBot], 3));
    v.computeVertexNormals();
    // Facing forward whichever way it was wound.
    v.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
    out.push(paint(v, shirt));
    if (has('lapels')) {
      for (const side of [-1, 1]) {
        const lapel = new THREE.BufferGeometry();
        lapel.setAttribute('position', new THREE.Float32BufferAttribute(
          side < 0
            ? [x0 - 0.06, yTop, zTop + 0.002, x0, yBot, zBot + 0.002, x0 - 0.1, yTop - 0.06, front(yTop - 0.06) + 0.002]
            : [x0 + 0.06, yTop, zTop + 0.002, x0 + 0.1, yTop - 0.06, front(yTop - 0.06) + 0.002, x0, yBot, zBot + 0.002],
          3
        ));
        lapel.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
        out.push(paint(lapel, outfit.lapel ?? 0x222222));
      }
    }
  }
  if (has('tie')) {
    const yTop = neck + 0.005;
    out.push(paint(box(x0, yTop - 0.012, front(yTop - 0.02) + 0.004, 0.026, 0.024, 0.012), outfit.tie ?? 0x7a2430));
    const length = has('vest') ? 0.15 : 0.24;
    const g = new THREE.BoxGeometry(0.034, length, 0.006);
    g.translate(x0, yTop - 0.02 - length / 2, front(yTop - 0.02 - length / 2) + 0.004);
    out.push(paint(g, outfit.tie ?? 0x7a2430));
  }
  if (has('bowtie')) {
    const y = neck + 0.002;
    const z = front(y - 0.02) + 0.006;
    for (const side of [-1, 1]) {
      const wing = new THREE.ConeGeometry(0.016, 0.034, 4);
      wing.rotateZ((side * Math.PI) / 2);
      wing.translate(x0 + side * 0.018, y, z);
      out.push(paint(wing, outfit.tie ?? 0x1a1a1a));
    }
  }
  if (has('neckerchief')) {
    const y = neck + 0.01;
    const knot = new THREE.TorusGeometry(0.055, 0.012, 5, 12, Math.PI);
    knot.rotateX(Math.PI / 2);
    knot.rotateZ(Math.PI);
    knot.translate(x0, y, front(y - 0.02) - 0.045);
    out.push(paint(knot, outfit.tie ?? 0xd0563f));
    out.push(paint(box(x0, y - 0.03, front(y - 0.03) + 0.004, 0.03, 0.04, 0.01), outfit.tie ?? 0xd0563f));
  }
  if (has('buttons')) {
    const colour = outfit.buttons ?? 0x2b2b2b;
    const double = outfit.kind === 'chef';
    for (let i = 0; i < 4; i += 1) {
      const y = neck - 0.06 - i * 0.07;
      for (const off of double ? [-0.045, 0.045] : [0]) {
        const b = new THREE.SphereGeometry(0.007, 6, 4);
        b.translate(x0 + off, y, front(y) + 0.002);
        out.push(paint(b, colour));
      }
    }
  }
  if (tag) {
    // The badge, over the heart (the body's left, +X): a gold plate, the
    // department's stripe across its top, and two dark lines that read as a
    // name at any distance a name could be read.
    const y = neck - 0.1;
    const x = x0 + 0.085;
    const z = front(y) + 0.003;
    out.push(paint(box(x, y, z, 0.07, 0.024, 0.004), 0xd9be7a));
    out.push(paint(box(x, y + 0.008, z + 0.0025, 0.07, 0.007, 0.002), tag.stripe));
    out.push(paint(box(x - 0.006, y - 0.002, z + 0.0025, 0.042, 0.0035, 0.0015), 0x2a2522));
    out.push(paint(box(x - 0.012, y - 0.008, z + 0.0025, 0.03, 0.0025, 0.0015), 0x4a4440));
  }
  return out;
}

/** What goes round the hips: the tool belt and its pouches, the apron. */
function hipPieces(m, pieces, outfit) {
  const out = [];
  const has = (p) => pieces.includes(p);
  const { waist } = m;
  if (has('toolbelt')) {
    const belt = new THREE.TorusGeometry(1, 0.06, 4, 20);
    belt.rotateX(Math.PI / 2);
    belt.scale(waist.rx * 1.06, 0.4, waist.rz * 1.12);
    belt.translate(0, waist.y - 0.015, waist.cz);
    out.push(paint(belt, outfit.belt ?? 0x5a3b22));
    for (const side of [-1, 1]) {
      out.push(paint(box(side * waist.rx * 0.95, waist.y - 0.06, waist.cz + waist.rz * 0.45, 0.05, 0.08, 0.06), outfit.belt ?? 0x5a3b22));
      // A hammer handle and a tape, so it reads as tools and not a purse.
      out.push(paint(box(side * waist.rx * 1.02, waist.y - 0.12, waist.cz + waist.rz * 0.15, 0.014, 0.12, 0.014), side < 0 ? 0x8a6a3a : 0xd8b030));
    }
  }
  if (has('apron')) {
    const top = waist.y + (outfit.kind === 'tunic' ? -0.02 : 0.03);
    const length = outfit.kind === 'chef' ? 0.32 : 0.24;
    // Round the front of the hips, not a board in front of them.
    const g = new THREE.CylinderGeometry(1, 1.08, length, 10, 1, true, -0.75, 1.5);
    g.scale(waist.rx * 1.12, 1, waist.rz * 1.3);
    g.translate(0, top - length / 2, waist.cz);
    out.push(paint(g, outfit.apron ?? 0xf0eee8));
    const tie = new THREE.TorusGeometry(1, 0.012, 3, 18);
    tie.rotateX(Math.PI / 2);
    tie.scale(waist.rx * 1.03, 1, waist.rz * 1.06);
    tie.translate(0, top - 0.008, waist.cz);
    out.push(paint(tie, outfit.apron ?? 0xf0eee8));
  }
  return out;
}

/**
 * The clothes, as one material with three zones read off where each vertex
 * is in the bind: the legs below the waist, the sleeves outboard of the
 * shoulders, the torso between. Past `sleeveEnd` (metres out from the middle)
 * an arm is bare, and below `hem` a leg is.
 */
export function clothMaterial(base, outfit, skin, m) {
  const zones = {
    uTorso: { value: new THREE.Color(outfit.torso ?? skin) },
    uSleeve: { value: new THREE.Color(outfit.sleeves ?? skin) },
    uLegs: { value: new THREE.Color(outfit.legs ?? 0x333333) },
    uSkin: { value: new THREE.Color(skin) },
    uWaist: { value: m.waist.y + 0.02 },
    uShoulder: { value: 0.215 },
    uSleeveEnd: { value: { long: 9, rolled: 0.5, short: 0.33, none: 0 }[outfit.sleeveEnd ?? 'long'] ?? 9 },
    uHem: { value: outfit.legEnd === 'shorts' ? 0.58 : -1 },
  };
  if (!outfit.torso) zones.uTorso.value.set(skin);
  const material = base.clone();
  material.color.set(0xffffff);
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, zones);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBind;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBind = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nvarying vec3 vBind;\nuniform vec3 uTorso;\nuniform vec3 uSleeve;\nuniform vec3 uLegs;\nuniform vec3 uSkin;\nuniform float uWaist;\nuniform float uShoulder;\nuniform float uSleeveEnd;\nuniform float uHem;'
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        float ax = abs(vBind.x);
        float upper = smoothstep(uWaist - 0.012, uWaist + 0.012, vBind.y);
        float arm = smoothstep(uShoulder - 0.012, uShoulder + 0.012, ax) * upper;
        vec3 zone = mix(mix(uLegs, uTorso, upper), uSleeve, arm);
        float bareArm = arm * smoothstep(uSleeveEnd - 0.01, uSleeveEnd + 0.01, ax);
        float bareLeg = (1.0 - upper) * (1.0 - smoothstep(uHem - 0.01, uHem + 0.01, vBind.y));
        diffuseColor.rgb *= mix(zone, uSkin, max(bareArm, bareLeg));`
      );
  };
  material.customProgramCacheKey = () => 'lc-cloth-zones';
  material.userData.zones = zones;
  return material;
}

/**
 * Builds every piece of a look and hangs it on the bones. Returns the meshes,
 * for disposing. `bindOf(name)` is a bone's inverse bind matrix.
 */
export function dressPieces({ look, m, bones, bindOf }) {
  const pieces = look.outfit.pieces ?? [];
  const hatted = pieces.some((p) => ['toque', 'cap', 'valetcap', 'sunhat'].includes(p));
  const groups = {
    Head: [
      ...(look.hair.style === 'bald' ? [] : hairPieces(m, look.hair.style, look.hair.colour, look.key, hatted)),
      ...facePieces(m, look.face, look.hair.colour, look.skin),
      ...headwear(m, pieces, look.outfit),
    ],
    Spine2: chestPieces(m, pieces, look.outfit, look.tag),
    Hips: hipPieces(m, pieces, look.outfit),
  };
  const meshes = [];
  for (const [name, list] of Object.entries(groups)) {
    const bone = bones[name];
    const inverse = bindOf(name);
    if (!bone || !inverse || !list.length) {
      for (const g of list) g.dispose();
      continue;
    }
    const merged = mergeGeometries(list.map(plain));
    for (const g of list) g.dispose();
    if (!merged) continue;
    merged.applyMatrix4(inverse);
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, piecesMaterial());
    mesh.name = `dress-${name}`;
    mesh.castShadow = true;
    bone.add(mesh);
    meshes.push(mesh);
  }
  return meshes;
}
